// src/components/screens/Dashboard.js
import {
  Activity,
  AlertCircle,
  Calendar,
  ChevronDown,
  Clock,
  Download,
  MapPin,
  MoreVertical,
  Plus,
  RefreshCw,
  UserCheck,
  Users,
  X
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/apiService';
import StatCard from '../common/StatCard';

const Dashboard = ({ onNavigateToTab }) => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState({
    officers: [],
    scans: [],
    stats: {
      totalOfficers: 0,
      activeOfficers: 0,
      totalScans: 0,
      todayScans: 0,
      successRate: 0,
      averageScansPerOfficer: 0,
      topPerformer: null
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddOfficerModal, setShowAddOfficerModal] = useState(false);
  const [addingOfficer, setAddingOfficer] = useState(false);
  const [officerForm, setOfficerForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    role: 'Officer'
  });
  const [formErrors, setFormErrors] = useState({});

  // Calculate statistics from the data
  const calculateStats = (officers, scans) => {
    const totalOfficers = officers.length;
    const activeOfficers = officers.filter(officer => officer.isActive).length;
    const totalScans = scans.length;
    
    // Calculate today's scans
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayScans = scans.filter(scan => {
      const scanDate = new Date(scan.createdAt);
      scanDate.setHours(0, 0, 0, 0);
      return scanDate.getTime() === today.getTime();
    }).length;

    // Calculate success rate
    const successfulScans = scans.filter(scan => scan.result === 'SUCCESS').length;
    const successRate = totalScans > 0 ? Math.round((successfulScans / totalScans) * 100) : 0;

    // Calculate average scans per officer
    const averageScansPerOfficer = totalOfficers > 0 ? Math.round(totalScans / totalOfficers) : 0;

    // Find top performer
    const scansByOfficer = {};
    scans.forEach(scan => {
      const officerId = scan.scannedBy._id;
      scansByOfficer[officerId] = (scansByOfficer[officerId] || 0) + 1;
    });

    let topPerformer = null;
    let maxScans = 0;
    Object.entries(scansByOfficer).forEach(([officerId, scanCount]) => {
      if (scanCount > maxScans) {
        maxScans = scanCount;
        const officer = officers.find(o => o._id === officerId);
        if (officer) {
          topPerformer = {
            ...officer,
            totalScans: scanCount
          };
        }
      }
    });

    return {
      totalOfficers,
      activeOfficers,
      totalScans,
      todayScans,
      successRate,
      averageScansPerOfficer,
      topPerformer
    };
  };

  // Fetch dashboard data
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Dashboard: Fetching data...');

      // Fetch officers and scans separately with individual error handling
      let officersResponse = null;
      let scansResponse = null;

      try {
        console.log('Dashboard: Fetching users...');
        officersResponse = await apiService.getUsers();
        console.log('Dashboard: Users response:', officersResponse);
      } catch (error) {
        console.error('Dashboard: Users API failed:', error);
        officersResponse = { success: false, data: [], error: error.message };
      }

      try {
        console.log('Dashboard: Fetching scans...');
        scansResponse = await apiService.getScans();
        console.log('Dashboard: Scans response:', scansResponse);
      } catch (error) {
        console.error('Dashboard: Scans API failed:', error);
        scansResponse = { success: false, logs: [], error: error.message };
      }

      console.log('Dashboard: API responses received', {
        officers: officersResponse,
        scans: scansResponse
      });

      // Extract data from responses with proper null checks
      const allUsers = (officersResponse && officersResponse.success && officersResponse.data) ? officersResponse.data : [];
      const officers = allUsers.filter(user => user.role === 'Officer'); // Filter for officers only
      const scans = (scansResponse && scansResponse.success && scansResponse.logs) ? scansResponse.logs : [];

      // Calculate statistics
      const stats = calculateStats(officers, scans);

      setDashboardData({
        officers,
        scans,
        stats
      });

      console.log('Dashboard: Data processed', { officers: officers.length, scans: scans.length, stats });

      // If both APIs failed, show error
      if (!officersResponse.success && !scansResponse.success) {
        throw new Error('Both Users and Scans APIs failed');
      }

    } catch (error) {
      console.error('Dashboard: Error fetching data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    
    if (!officerForm.firstName.trim()) {
      errors.firstName = 'First name is required';
    }
    
    if (!officerForm.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }
    
    if (!officerForm.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(officerForm.email)) {
      errors.email = 'Email is invalid';
    }
    
    if (!officerForm.mobile.trim()) {
      errors.mobile = 'Mobile number is required';
    } else if (!/^\d{10}$/.test(officerForm.mobile)) {
      errors.mobile = 'Mobile number should be 10 digits';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Mobile is entered as the 10-digit local number only — the +91 prefix
    // is fixed in the UI and prepended when the payload is built, matching
    // the format the backend requires.
    const nextValue = name === 'mobile'
      ? value.replace(/[^0-9]/g, '').slice(0, 10)
      : value;
    setOfficerForm(prev => ({
      ...prev,
      [name]: nextValue
    }));
    
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle add officer
  const handleAddOfficer = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setAddingOfficer(true);
      
      const payload = { ...officerForm, mobile: `+91${officerForm.mobile}` };
      console.log('Adding officer:', payload);

      const response = await apiService.registerEmployee(payload);
      
      if (response.success) {
        console.log('Officer added successfully:', response);
        
        // Reset form
        setOfficerForm({
          firstName: '',
          lastName: '',
          email: '',
          mobile: '',
          role: 'Officer'
        });
        
        // Close modal
        setShowAddOfficerModal(false);
        
        // Refresh dashboard data
        await fetchDashboardData();
        
        // You can show a success message here if you have a toast/notification system
        alert('Officer added successfully!');
        
      } else {
        throw new Error(response.message || 'Failed to add officer');
      }
      
    } catch (error) {
      console.error('Error adding officer:', error);
      alert(`Error adding officer: ${error.message}`);
    } finally {
      setAddingOfficer(false);
    }
  };

  // Close modal
  const closeModal = () => {
    setShowAddOfficerModal(false);
    setOfficerForm({
      firstName: '',
      lastName: '',
      email: '',
      mobile: '',
      role: 'Officer'
    });
    setFormErrors({});
  };

  // Format timestamp for display
  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format location for display
  const formatLocation = (scan) => {
    if (scan.meta?.location) {
      return `${scan.meta.location.lat.toFixed(3)}, ${scan.meta.location.lng.toFixed(3)}`;
    }
    return 'Unknown Location';
  };

  // Get scan type from scan data
  const getScanType = (scan) => {
    if (scan.code?.includes('QR')) {
      return 'QR Code';
    }
    return 'Manual Scan';
  };

  // Load data on component mount
  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Refresh data manually
  const handleRefresh = () => {
    fetchDashboardData();
  };

  if (loading) {
    return (
      <div className="space-y-8 animate-fade-in">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="skeleton h-8 w-48"></div>
            <div className="skeleton h-4 w-64"></div>
          </div>
          <div className="skeleton h-10 w-32 rounded-xl"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[0, 1, 2, 3].map((i) => (
            <StatCard key={i} loading />
          ))}
        </div>
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
          {[0, 1].map((i) => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200/50 dark:border-gray-700/50 p-6 space-y-4">
              <div className="skeleton h-5 w-40"></div>
              {[0, 1, 2].map((j) => (
                <div key={j} className="flex items-center space-x-4">
                  <div className="skeleton w-12 h-12 rounded-xl flex-shrink-0"></div>
                  <div className="flex-1 space-y-2">
                    <div className="skeleton h-4 w-1/2"></div>
                    <div className="skeleton h-3 w-1/3"></div>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 dark:text-red-400 mb-4">Error loading dashboard: {error}</p>
          <button
            onClick={handleRefresh}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between animate-fade-in-up">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
            Dashboard
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Welcome back, {user?.firstName}! Here's your system overview
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowAddOfficerModal(true)}
            className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl hover:from-green-600 hover:to-emerald-700 flex items-center space-x-2 transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-[1.03] active:scale-[0.98]"
          >
            <Plus className="h-4 w-4" />
            <span>Add Officer</span>
          </button>
          <button
            onClick={handleRefresh}
            className="group px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center space-x-2 transition-all duration-200 hover:scale-[1.03] active:scale-[0.98]"
          >
            <RefreshCw className="h-4 w-4 transition-transform duration-500 group-hover:rotate-180" />
            <span>Refresh</span>
          </button>
          <button className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center space-x-2 transition-all duration-200 hover:scale-[1.03] active:scale-[0.98]">
            <Download className="h-4 w-4" />
            <span>Export</span>
          </button>
          <button className="px-4 py-2 bg-gradient-to-r from-primary-500 to-accent-600 text-white rounded-xl hover:from-primary-600 hover:to-accent-700 flex items-center space-x-2 transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-[1.03] active:scale-[0.98]">
            <Calendar className="h-4 w-4" />
            <span>Today</span>
          </button>
        </div>
      </div>

      {/* Add Officer Modal */}
      {showAddOfficerModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-fade-in" onClick={closeModal}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-md mx-4 animate-scale-in shadow-soft-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">Add New Officer</h3>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all duration-200 hover:rotate-90"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <form onSubmit={handleAddOfficer} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  First Name *
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={officerForm.firstName}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all duration-200 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                    formErrors.firstName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter first name"
                />
                {formErrors.firstName && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.firstName}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Last Name *
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={officerForm.lastName}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all duration-200 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                    formErrors.lastName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter last name"
                />
                {formErrors.lastName && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.lastName}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={officerForm.email}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all duration-200 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                    formErrors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter email address"
                />
                {formErrors.email && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.email}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Mobile Number *
                </label>
                <div className={`flex items-center border rounded-lg focus-within:ring-2 focus-within:ring-primary-500 dark:bg-gray-700 ${
                  formErrors.mobile ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}>
                  <span className="pl-3 pr-2 text-sm font-medium text-gray-500 dark:text-gray-400 border-r border-gray-300 dark:border-gray-600">
                    +91
                  </span>
                  <input
                    type="tel"
                    name="mobile"
                    value={officerForm.mobile}
                    onChange={handleInputChange}
                    maxLength={10}
                    className="w-full px-3 py-2 bg-transparent focus:outline-none dark:text-white"
                    placeholder="9000000000"
                  />
                </div>
                {formErrors.mobile && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.mobile}</p>
                )}
              </div>
              
              <div className="flex items-center justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addingOfficer}
                  className="px-6 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg hover:from-green-600 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 transition-all duration-200"
                >
                  {addingOfficer ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      <span>Adding...</span>
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4" />
                      <span>Add Officer</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Officers"
          value={dashboardData.stats.totalOfficers}
          change={`${dashboardData.stats.activeOfficers} active`}
          icon={Users}
          color="blue"
          delay={0}
          onClick={() => onNavigateToTab('officers')}
        />
        <StatCard
          title="Active Officers"
          value={dashboardData.stats.activeOfficers}
          change={`${Math.round((dashboardData.stats.activeOfficers / Math.max(dashboardData.stats.totalOfficers, 1)) * 100)}% active rate`}
          icon={UserCheck}
          color="green"
          delay={60}
        />
        <StatCard
          title="Total Scans"
          value={dashboardData.stats.totalScans}
          change={`${dashboardData.stats.successRate}% success rate`}
          icon={Activity}
          color="purple"
          delay={120}
          onClick={() => onNavigateToTab('scan-history')}
        />
        <StatCard
          title="Today's Scans"
          value={dashboardData.stats.todayScans}
          change={`${dashboardData.stats.averageScansPerOfficer} avg per officer`}
          icon={Clock}
          color="yellow"
          delay={180}
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
        {/* Recent Officers */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200/50 dark:border-gray-700/50 p-6 hover:shadow-xl transition-all duration-300 animate-fade-in-up" style={{ animationDelay: '150ms' }}>
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">Recent Officers</h3>
            <button
              onClick={() => onNavigateToTab('officers')}
              className="text-primary-600 hover:text-primary-700 font-medium text-sm flex items-center space-x-1 hover:scale-105 hover:translate-x-0.5 transition-all"
            >
              <span>View All</span>
              <ChevronDown className="h-4 w-4 rotate-[-90deg]" />
            </button>
          </div>
          <div className="space-y-4">
            {dashboardData.officers.slice(0, 4).map((officer, index) => (
              <div
                key={officer._id}
                style={{ '--stagger': index }}
                className="group flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 hover-lift stagger-in"
              >
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-accent-600 rounded-xl flex items-center justify-center shadow-lg transition-transform duration-200 group-hover:scale-105">
                    <span className="text-white font-bold text-sm">
                      {officer.firstName?.charAt(0)?.toUpperCase()}{officer.lastName?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-white">
                      {officer.firstName} {officer.lastName}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{officer.email}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <span className={`px-3 py-1 text-xs font-bold rounded-full ${
                    officer.isActive 
                      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300' 
                      : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                  }`}>
                    {officer.isActive ? 'Active' : 'Inactive'}
                  </span>
                  <button className="opacity-0 group-hover:opacity-100 p-1 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-all">
                    <MoreVertical className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
            {dashboardData.officers.length === 0 && (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                No officers found
              </div>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200/50 dark:border-gray-700/50 p-6 hover:shadow-xl transition-all duration-300 animate-fade-in-up" style={{ animationDelay: '200ms' }}>
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">Recent Activity</h3>
            <button
              onClick={() => onNavigateToTab('scan-history')}
              className="text-primary-600 hover:text-primary-700 font-medium text-sm flex items-center space-x-1 hover:scale-105 hover:translate-x-0.5 transition-all"
            >
              <span>View All</span>
              <ChevronDown className="h-4 w-4 rotate-[-90deg]" />
            </button>
          </div>
          <div className="space-y-4">
            {dashboardData.scans.slice(0, 5).map((scan, index) => (
              <div
                key={scan._id}
                style={{ '--stagger': index }}
                className="flex items-start space-x-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 hover-lift stagger-in"
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  scan.result === 'SUCCESS'
                    ? 'bg-green-100 dark:bg-green-900/50'
                    : 'bg-red-100 dark:bg-red-900/50'
                }`}>
                  <Activity className={`h-5 w-5 ${
                    scan.result === 'SUCCESS'
                      ? 'text-green-600 dark:text-green-400'
                      : 'text-red-600 dark:text-red-400'
                  }`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 dark:text-white text-sm">
                    {scan.scannedBy ? `${scan.scannedBy.firstName || ''} ${scan.scannedBy.lastName || ''}`.trim() || 'Unknown Officer' : 'Unknown Officer'}
                  </p>
                  <div className="flex items-center space-x-2 mt-1">
                    <MapPin className="h-3 w-3 text-gray-400" />
                    <span className="text-xs text-gray-500 dark:text-gray-400">{formatLocation(scan)}</span>
                    <span className="text-xs text-gray-400">•</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{formatTime(scan.createdAt)}</span>
                  </div>
                </div>
                <div className="flex flex-col items-end space-y-1">
                  <span className={`px-2 py-1 text-xs font-medium rounded-lg ${
                    scan.result === 'SUCCESS' 
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300' 
                      : 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300'
                  }`}>
                    {scan.result}
                  </span>
                  {scan.isVerified && (
                    <span className="px-2 py-1 text-xs font-medium rounded-lg bg-primary-100 text-primary-700 dark:bg-primary-900/50 dark:text-primary-300">
                      Verified
                    </span>
                  )}
                </div>
              </div>
            ))}
            {dashboardData.scans.length === 0 && (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                No recent activity
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Performance Overview */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200/50 dark:border-gray-700/50 p-6 hover:shadow-xl transition-all duration-300 animate-fade-in-up" style={{ animationDelay: '250ms' }}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">Performance Overview</h3>
          <button
            onClick={() => onNavigateToTab('reports')}
            className="text-primary-600 hover:text-primary-700 font-medium text-sm flex items-center space-x-1 hover:scale-105 hover:translate-x-0.5 transition-all"
          >
            <span>View Reports</span>
            <ChevronDown className="h-4 w-4 rotate-[-90deg]" />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center p-4 bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 rounded-xl border border-primary-200/50 dark:border-primary-700/50 hover-lift hover:shadow-soft">
            <h4 className="text-sm font-medium text-primary-700 dark:text-primary-300 mb-1">Success Rate</h4>
            <p className="text-2xl font-bold text-primary-900 dark:text-primary-100">{dashboardData.stats.successRate}%</p>
            <p className="text-xs text-primary-600 dark:text-primary-400 mt-1">
              {dashboardData.scans.filter(s => s.result === 'SUCCESS').length} successful scans
            </p>
          </div>

          <div className="text-center p-4 bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-900/20 dark:to-emerald-800/20 rounded-xl border border-emerald-200/50 dark:border-emerald-700/50 hover-lift hover:shadow-soft">
            <h4 className="text-sm font-medium text-emerald-700 dark:text-emerald-300 mb-1">Avg. Scans/Officer</h4>
            <p className="text-2xl font-bold text-emerald-900 dark:text-emerald-100">{dashboardData.stats.averageScansPerOfficer}</p>
            <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">Per active officer</p>
          </div>

          <div className="text-center p-4 bg-gradient-to-br from-accent-50 to-accent-100 dark:from-accent-900/20 dark:to-accent-800/20 rounded-xl border border-accent-200/50 dark:border-accent-700/50 hover-lift hover:shadow-soft">
            <h4 className="text-sm font-medium text-accent-700 dark:text-accent-300 mb-1">Top Performer</h4>
            {dashboardData.stats.topPerformer ? (
              <>
                <p className="text-lg font-bold text-accent-900 dark:text-accent-100">
                  {dashboardData.stats.topPerformer.firstName} {dashboardData.stats.topPerformer.lastName}
                </p>
                <p className="text-xs text-accent-600 dark:text-accent-400 mt-1">
                  {dashboardData.stats.topPerformer.totalScans} scans
                </p>
              </>
            ) : (
              <p className="text-lg font-bold text-accent-900 dark:text-accent-100">No data</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;